import { useState, useMemo, type Dispatch, type SetStateAction } from 'react';

type Toggle = {
	toggle(): void;
};

type SwitchOn = {
	on(): void;
};

type SwitchOff = {
	off(): void;
};

type SetState = {
	setValue: Dispatch<SetStateAction<boolean>>;
};

type Switch = SwitchOn & SwitchOff;
type Handlers = Toggle & Switch & SetState;

/**
 * A react hook that handles boolean state with useful utility functions.
 *
 * @example
 * ```tsx
 * function Example() {
 *   const [isHovering, setIsHovering] = useBoolean(false);
 *
 *   return (
 *     <div onMouseEnter={setIsHovering.on} onMouseLeave={setIsHovering.off}>
 *       {isHovering ? 'Is hovering' : 'Is not hovering'}
 *     </div>
 *   )
 * }
 * ```
 *
 * @example
 * ```tsx
 * function Example() {
 *   const [isOpen, { off: handleClose, toggle }] = useBoolean(false);
 *
 *   return (
 *     <Popup
 *       isOpen={isOpen}
 *       onClose={handleClose}
 *       placement="bottom-start"
 *       content={() => (<p>Hello world!</p>)}
 *       trigger={(triggerProps) => (
 *         <Button
 *           {...triggerProps}
 *           appearance="primary"
 *           onClick={toggle}
 *         >
 *            Toggle
 *         </Button>
 *       )}
 *     />
 *   )
 * }
 * ```
 *
 * @param initialValue - Initial state. Set to `false` if no value is passed.
 * @returns A stateful value and utility functions to update the state.
 */
export function useBoolean(initialValue = false): [boolean, Handlers] {
	if (typeof initialValue !== 'boolean') {
		throw new Error('initialValue must be `true` or `false`');
	}
	const [value, setValue] = useState(initialValue);

	const handlers = useMemo<Handlers>(
		() => ({
			toggle: () => setValue((prevValue) => !prevValue),
			on: () => setValue(true),
			off: () => setValue(false),
			setValue,
		}),
		[],
	);

	return [value, handlers];
}
